import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { catchError, exhaustMap, finalize, map, of, switchMap, tap, withLatestFrom } from 'rxjs'
import * as WidgetPagesActions from './widget-pages.actions'

import { Router } from '@angular/router'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import { GbErrorMessageService } from '@galabid/shared/utils/helpers'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { AuctionsConstants } from 'libs/gb-dashboard/auctions/feature/src/lib/auctions.constants'
import { BrandingConstants } from 'libs/gb-dashboard/branding/feature/src/lib/branding.constants'
import { WidgetPagesApiService } from './widget-pages-api.service'
import { WIDGET_PAGES_LOADER } from './widget-pages.models'
import { GbNavigationService } from 'libs/shared/utils/services/src/lib/gb-navigation-service/gb-navigation.service'
import { GbLoaderService } from 'libs/shared/utils/loader/src/lib/services/gb-loader.service'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'

@Injectable()
export class WidgetPagesEffects {
  private actions$ = inject(Actions)

  loadWidgetPages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WidgetPagesActions.loadWidgetPages),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) => {
        return this.widgetPagesApiService.getWidgetPages(urlName).pipe(
          map((response) =>
            WidgetPagesActions.loadWidgetPagesSuccess({
              widgetPages: response,
            }),
          ),
          catchError((error) => {
            return of(WidgetPagesActions.loadWidgetPagesFailure({ error }))
          }),
        )
      }),
    ),
  )

  loadOldWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WidgetPagesActions.loadOldWidgets),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) => {
        return this.widgetPagesApiService.getOldWidgetPages(urlName).pipe(
          map((response) =>
            WidgetPagesActions.loadOldWidgetsSuccess({
              old_widgets: response,
            }),
          ),
          catchError((error) => {
            return of(WidgetPagesActions.loadOldWidgetsFailure({ error }))
          }),
        )
      }),
    ),
  )

  goToWidgetPagesList$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WidgetPagesActions.goToWidgetPagesList),
        withLatestFrom(this.selectedAuctionFacade.urlName$),
        tap(([action, urlName]) => {
          const fragments = [
            `/${AuctionsConstants.AUCTION}/${urlName}/${BrandingConstants.BRANDING}/${BrandingConstants.WIDGET_PAGES}`,
          ]
          const options = action.newTab ? { newTab: action.newTab } : {}
          this.gbNavigationService.navigate(fragments, options)
        }),
      ),
    { dispatch: false },
  )

  syncWidgetPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WidgetPagesActions.syncWidgetPage),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([{ uuid }, urlName]) => {
        return this.widgetPagesApiService.syncWidgetPage(urlName, uuid).pipe(
          map((widgetPage) =>
            WidgetPagesActions.syncWidgetPageSuccess({
              widgetPage,
            }),
          ),
          catchError((error) => {
            return of(WidgetPagesActions.syncWidgetPageFailure({ error }))
          }),
        )
      }),
    ),
  )

  addWidgetPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WidgetPagesActions.addWidgetPage),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) => {
        return this.widgetPagesApiService
          .addWidgetPage(urlName, {
            ...action.widgetPage,
            auction: urlName,
          })
          .pipe(
            map((response) => {
              this.gbNotificationService.success('WIDGETS.ADD_WIDGET_PAGE_SUCCESS')
              return WidgetPagesActions.addWidgetPageSuccess({
                widgetPage: response,
                onSuccess: action.onSuccess,
              })
            }),
            catchError((error) => {
              return of(WidgetPagesActions.addWidgetPageFailure({ error }))
            }),
          )
      }),
    ),
  )

  updateWidgetPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WidgetPagesActions.updateWidgetPage),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) => {
        return this.widgetPagesApiService
          .updateWidgetPage(urlName, {
            ...action.widgetPage,
            auction: urlName,
          })
          .pipe(
            map((response) => {
              this.gbNotificationService.success('WIDGETS.UPDATE_WIDGET_PAGE_SUCCESS')
              return WidgetPagesActions.updateWidgetPageSuccess({
                widgetPage: response,
                onSuccess: action.onSuccess,
              })
            }),
            catchError((error) => {
              return of(WidgetPagesActions.updateWidgetPageFailure({ error }))
            }),
          )
      }),
    ),
  )

  deleteWidgetPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WidgetPagesActions.deleteWidgetPage),
      exhaustMap((action) =>
        this.gbDashboardDialogService.filterByConfirmDialog(
          {
            title: 'WIDGETS.DELETE_WIDGET_PAGE_TITLE',
            body: 'WIDGETS.DELETE_WIDGET_PAGE_DESC',
            confirmButtonText: 'YES',
            cancelButtonText: 'NO',
          },
          action,
        ),
      ),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      tap(() =>
        this.gbLoaderService.showLoaderDialog(
          WIDGET_PAGES_LOADER.deleteWidgetPage.id,
          WIDGET_PAGES_LOADER.deleteWidgetPage.message,
        ),
      ),
      switchMap(([action, urlName]) =>
        this.widgetPagesApiService.deleteWidgetPage(urlName, action.uuid).pipe(
          map(() => WidgetPagesActions.deleteWidgetPageSuccess({ uuid: action.uuid })),
          catchError((error) => of(WidgetPagesActions.deleteWidgetPageFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(WIDGET_PAGES_LOADER.deleteWidgetPage.id)),
        ),
      ),
    ),
  )

  addWidgetPageSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WidgetPagesActions.addWidgetPageSuccess),
        withLatestFrom(this.selectedAuctionFacade.urlName$),
        tap(([action, urlName]) => {
          if (action.onSuccess) action.onSuccess(action.widgetPage.uuid)

          this.router.navigate([
            AuctionsConstants.AUCTION,
            urlName,
            BrandingConstants.BRANDING,
            BrandingConstants.WIDGET_PAGES,
          ])
        }),
      ),
    { dispatch: false },
  )

  updateWidgetPageSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WidgetPagesActions.updateWidgetPageSuccess),
        tap((action) => {
          if (action.onSuccess) action.onSuccess()
        }),
      ),
    { dispatch: false },
  )

  goToAddOrEditWidgetPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WidgetPagesActions.goToAddOrEditWidgetPage),
        withLatestFrom(this.selectedAuctionFacade.urlName$),
        map(([action, urlName]) => {
          let path = [
            AuctionsConstants.AUCTION,
            urlName,
            BrandingConstants.BRANDING,
            action.uuid ? BrandingConstants.EDIT_WIDGET_PAGE : BrandingConstants.ADD_WIDGET_PAGE,
          ]
          if (action.uuid) path = [...path, action.uuid]
          this.gbNavigationService.navigate(path, { newTab: action.newTab })
        }),
      ),
    { dispatch: false },
  )

  showErrorToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          WidgetPagesActions.loadWidgetPagesFailure,
          WidgetPagesActions.addWidgetPageFailure,
          WidgetPagesActions.updateWidgetPageFailure,
          WidgetPagesActions.deleteWidgetPageFailure,
          WidgetPagesActions.syncWidgetPageFailure,
        ),
        map((action) => {
          if (!action.error) return
          let errorObject = action.error['error']
          if (action.error.error && action.error.error.widget_buttons) {
            for (let widget of action.error.error.widget_buttons) {
              if (Object.keys(widget).length > 0) {
                errorObject = widget
                break
              }
            }
          }
          const errorMessage = this.gbErrorMessageService.getErrorMessage(errorObject)
          if (errorMessage && typeof errorMessage === 'string') this.gbNotificationService.error(errorMessage)
          else {
            this.gbNotificationService.error('GENERIC.ERROR')
          }
        }),
      ),
    { dispatch: false },
  )

  constructor(
    private gbNotificationService: GbNotificationService,
    private gbErrorMessageService: GbErrorMessageService,
    private gbDashboardDialogService: GbDashboardDialogService,
    private gbLoaderService: GbLoaderService,
    private selectedAuctionFacade: SelectedAuctionFacade,
    private widgetPagesApiService: WidgetPagesApiService,
    private router: Router,
    private gbNavigationService: GbNavigationService,
  ) {}
}
